/**
 * Parse USEBIO XML files (Bridge event results)
 * Format specification: USEBIO 1.2 XML format from EBUScore
 */

import { recalculatePairScores } from './pairsScoring';
import { mapPlayerNames } from './playerNameMapper';

export async function parseUSEBIO(xmlText) {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlText, 'text/xml');

  // Check for parsing errors
  const parserError = xmlDoc.querySelector('parsererror');
  if (parserError) {
    throw new Error('Invalid XML format');
  }

  const eventNode = xmlDoc.querySelector('EVENT');
  if (!eventNode) {
    throw new Error('No EVENT node found in XML');
  }

  // Extract metadata
  const metadata = {
    Title: eventNode.querySelector('EVENT_DESCRIPTION')?.textContent || 'Untitled Game',
    Date: eventNode.querySelector('DATE')?.textContent || '',
    Director: eventNode.querySelector('DIRECTOR')?.textContent || '',
    Country: eventNode.querySelector('COUNTRY')?.textContent || '',
    Boards: eventNode.querySelector('BOARDS_PLAYED')?.textContent || '',
    Version: `${eventNode.querySelector('PROGRAM_NAME')?.textContent || ''} ${eventNode.querySelector('PROGRAM_VERSION')?.textContent || ''}`.trim(),
    Club: xmlDoc.querySelector('CLUB_NAME')?.textContent || '',
    EventType: eventNode.getAttribute('EVENT_TYPE') || 'PAIRS'
  };

  // Determine game type and scoring method from EVENT_TYPE
  const eventType = metadata.EventType.toUpperCase();
  let gameType = 'pairs';
  if (eventType.includes('TEAM') || eventType === 'KO' || eventType === 'LADDER' || eventType === 'AGGREGATE') {
    gameType = 'teams';
  } else if (eventType.includes('PAIR') || eventType.includes('IMP') || eventType.includes('BUTLER') || eventType === 'INDIVIDUAL') {
    gameType = 'pairs';
  }

  // Set Scoring metadata: '2' = IMP, '0' = MP (matches EBUScore CSV convention)
  const impTypes = ['CROSS_IMP', 'BUTLER_PAIRS', 'SWISS_PAIRS_CROSS_IMPS', 'SWISS_PAIRS_BUTLER_IMPS'];
  if (gameType === 'pairs') {
    metadata.Scoring = impTypes.includes(eventType) ? '2' : '0';
  }

  // Parse scores - match EBU parser format
  const scores = [];
  const pairNodes = eventNode.querySelectorAll('PAIR');

  pairNodes.forEach(pairNode => {
    const pairNumber = pairNode.querySelector('PAIR_NUMBER')?.textContent || '';
    const position = pairNode.querySelector('PLACE')?.textContent || '';
    const percentage = pairNode.querySelector('PERCENTAGE')?.textContent || '';
    const totalScore = pairNode.querySelector('TOTAL_SCORE')?.textContent || '';

    // Get player names
    const playerNodes = pairNode.querySelectorAll('PLAYER');
    const players = [];
    playerNodes.forEach(playerNode => {
      const name = playerNode.querySelector('PLAYER_NAME')?.textContent || '';
      if (name) {
        players.push(name);
      }
    });

    scores.push({
      position: position,
      pair: pairNumber,
      players: players,
      score: parseFloat(totalScore) || parseFloat(percentage) || 0,
      percent: parseFloat(percentage) || 0,
    });
  });

  // Apply name mapping to all player names in scores
  for (const score of scores) {
    if (score.players && Array.isArray(score.players)) {
      score.players = await mapPlayerNames(score.players);
    }
  }

  // Build pair name lookup for traveller player names
  const pairNamesMap = {};
  scores.forEach(score => {
    if (score.pair) {
      pairNamesMap[score.pair] = score.players.join(' - ');
    }
  });

  // Parse travellers (board results) - match EBU parser format
  const travellers = {};
  const boardNodes = eventNode.querySelectorAll('BOARD');

  boardNodes.forEach(boardNode => {
    const boardNumber = boardNode.querySelector('BOARD_NUMBER')?.textContent || '';
    const travellerLines = [];

    const travellerLineNodes = boardNode.querySelectorAll('TRAVELLER_LINE');
    travellerLineNodes.forEach(lineNode => {
      const ns = lineNode.querySelector('NS_PAIR_NUMBER')?.textContent || '';
      const ew = lineNode.querySelector('EW_PAIR_NUMBER')?.textContent || '';
      const contract = lineNode.querySelector('CONTRACT')?.textContent || '';
      const declarer = lineNode.querySelector('PLAYED_BY')?.textContent || '';
      const tricks = lineNode.querySelector('TRICKS')?.textContent || '';
      const scoreRaw = lineNode.querySelector('SCORE')?.textContent || '';
      const nsMp = lineNode.querySelector('NS_MATCH_POINTS')?.textContent || '';
      const ewMp = lineNode.querySelector('EW_MATCH_POINTS')?.textContent || '';

      // USEBIO SCORE is from NS perspective: positive = NS score, negative = EW score
      let nsScore = '';
      let ewScore = '';
      if (scoreRaw) {
        const val = parseFloat(scoreRaw);
        if (val >= 0) {
          nsScore = scoreRaw;
          ewScore = val === 0 ? '0' : `-${scoreRaw}`;
        } else {
          nsScore = scoreRaw;
          ewScore = Math.abs(val).toString();
        }
      }

      travellerLines.push({
        ns: ns,
        ew: ew,
        nsScore: nsScore,
        ewScore: ewScore,
        nsPts: parseFloat(nsMp) || 0,
        ewPts: parseFloat(ewMp) || 0,
        contract: contract,
        made: tricks,
        declarer: declarer,
        nsPlayers: pairNamesMap[ns] || '',
        ewPlayers: pairNamesMap[ew] || '',
      });
    });

    if (travellerLines.length > 0) {
      travellers[boardNumber] = travellerLines;
    }
  });

  // Recalculate pair scores from travellers to get correct totalMPs
  if (gameType === 'pairs' && Object.keys(travellers).length > 0) {
    const recalculatedScores = recalculatePairScores(travellers);
    if (recalculatedScores.length > 0) {
      scores.forEach(score => {
        const recalc = recalculatedScores.find(r => r.pair === score.pair);
        if (recalc) {
          score.score = recalc.totalMPs;
          score.percent = recalc.percentage;
          score.position = recalc.position.toString();
        }
      });
    }
  }

  return {
    metadata,
    gameType,
    scores,
    travellers
  };
}

/**
 * Parse date from USEBIO format (DD/MM/YYYY) to ISO format (YYYY-MM-DD)
 */
export function parseUSEBIODate(dateStr) {
  if (!dateStr) return new Date().toISOString().split('T')[0];

  const parts = dateStr.split('/');
  if (parts.length === 3) {
    const [day, month, year] = parts;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  return new Date().toISOString().split('T')[0];
}
